import {EventType, tauriManager} from "./tauri";
import {electronManager} from "./electron";
import {samsungManager} from "./samsung";
import type {EventPayloads} from "./IntegrationEventTypes.ts";
import type {ScreenshotPolicy} from "../types.ts";
import {randomUuid} from "../utils/RandomUuid.ts";
import {BUNDLE_VERSION} from "../utils/BundleVersion.ts";
import type {ClientDeviceInfo} from "../utils/DeviceInfo.ts";

export type RuntimeLayer = "Tauri" | "Electron" | "Samsung" | "WebMode";

/** What IntegrationManager needs from a native integration. */
interface NativeIntegration {
    getVersion(): string | null;
    request<T extends EventType>(type: T, payload: EventPayloads[T]): Promise<unknown>;
    emit<T extends EventType>(type: T, payload: EventPayloads[T]): Promise<void>;
}

/**
 * One client bundle runs everywhere: in a browser, inside the Tauri and Electron shells, and as the
 * Samsung widget. The integration is picked at runtime, Tauri first, then Electron, then Samsung.
 */
export class IntegrationManager {
    private initialized = false;
    private initializing: Promise<void> | null = null;
    private native: NativeIntegration | null = null;
    private layer: RuntimeLayer = "WebMode";

    public init(): Promise<void> {
        if (!this.initialized) {
            this.initialized = true;
            this.initializing = this.probe();
        }
        return this.initializing ?? Promise.resolve();
    }

    private async probe(): Promise<void> {
        await tauriManager.init();
        if (tauriManager.isAvailable()) return this.use(tauriManager, "Tauri");

        await electronManager.init();
        if (electronManager.isAvailable()) return this.use(electronManager, "Electron");

        await samsungManager.init();
        if (samsungManager.isAvailable()) this.use(samsungManager, "Samsung");
    }

    private use(native: NativeIntegration, layer: RuntimeLayer): void {
        this.native = native;
        this.layer = layer;
    }

    public async dispose(): Promise<void> {
        if (!this.initialized) return;
        await tauriManager.dispose();
        await electronManager.dispose();
        await samsungManager.dispose();
        this.initialized = false;
        this.initializing = null;
        this.native = null;
        this.layer = "WebMode";
    }

    public hasIntegrated() {
        return this.native !== null;
    }

    /** Whether a reboot request can do anything. Samsung panels only allow it once the capability is on. */
    public canReboot(): boolean {
        if (this.layer === "Samsung") return samsungManager.capabilities().reboot;
        return this.native !== null;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c079 */
    public getRuntimeLayer(): RuntimeLayer {
        return this.layer;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c080 */
    public getRuntimeVersion(): string {
        return this.native?.getVersion() ?? BUNDLE_VERSION ?? "unknown";
    }

    /** Device details only the native layer knows; they win over what the user agent suggests. Waits for the probe. */
    public async getDeviceInfo(): Promise<ClientDeviceInfo> {
        await this.init();
        if (this.layer === "Samsung") return samsungManager.deviceInfo();
        return {};
    }

    async captureScreen(device_id: string) {
        await this.native?.request(EventType.ScreenshotRequest, {
            deviceId: device_id, requestId: randomUuid(),
        });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c763 */
    async sendScreenshotPolicy(policy: ScreenshotPolicy, deviceId: string | null) {
        // deviceId is not part of the backend's policy shape: the shell needs it
        // for the offload URL path and holds no device identity of its own.
        await this.native?.emit(EventType.ScreenshotPolicy, {
            interval: policy.interval,
            retention: policy.retention,
            offloadTimestamp: {
                hour: policy.offload_timestamp.hour,
                minute: policy.offload_timestamp.minute,
                second: policy.offload_timestamp.second,
            },
            deviceId: deviceId ?? undefined,
        });
    }

    /* DCS `device_action.request_audit_upload`. The captures live in the native
       shell's own store, so this is a no-op without an integration: in web mode
       there is nothing on disk to hand over. */
    async requestAuditUpload(deviceId: string | null) {
        // The shell falls back to the device id on the capture policy when this
        // is empty, which is the case until the socket has authenticated.
        await this.native?.request(EventType.AuditSend, {deviceId: deviceId ?? "", requestId: randomUuid()});
    }

    async rebootDevice() {
        await this.native?.request(EventType.RebootRequest, {deviceId: "", requestId: randomUuid()});
    }

    async monitor(cmd: string, data: string) {
        await this.native?.request(EventType.MonitorRequest, {deviceId: "", cmd, data, requestId: randomUuid()});
    }
}

export const integrationManager = new IntegrationManager();
